<template>
  <div class="buy-order-details">
    <div v-if="buyDetails">
      <div class="row g-2 g-lg-3 mb-3">
        <div class="col-xl-4">
          <div class="details-panel details-panel-summary h-100">
            <div class="d-flex align-items-center mb-2">
              <FontAwesomeIcon icon="shopping-cart" class="text-primary me-2" />
              <span class="text-uppercase text-muted fw-semibold section-label">
                {{ $t("company_orders.buy.order_summary") }}
              </span>
            </div>

            <div class="summary-content">
              <div class="text-muted small mb-1">
                {{ $t("company_orders.buy.volume") }}
              </div>
              <div class="fs-2 fw-semibold lh-sm">
                {{ buyDetails.volume }}
                <span class="fs-6 text-muted fw-normal">MWh</span>
              </div>

              <div
                v-if="pricePerMwh !== null"
                class="summary-detail summary-detail-secondary"
              >
                <div
                  class="text-muted small text-uppercase summary-detail-label"
                >
                  {{ $t("company_orders.buy.total_price_per_mwh") }}
                </div>
                <div class="summary-detail-value summary-detail-value-muted">
                  {{ formatEuro(pricePerMwh) }}/MWh
                </div>
              </div>

              <div class="summary-detail summary-detail-price">
                <div
                  class="text-muted small text-uppercase summary-detail-label"
                >
                  {{ $t("company_orders.buy.total_price") }}
                </div>
                <div
                  v-if="displayTotalPrice !== undefined"
                  class="summary-detail-value"
                >
                  {{ formatEuro(displayTotalPrice) }}
                </div>
                <RfqStatusTimeline
                  v-else-if="showRfqTimeline"
                  :createdAt="orderCreatedAt!"
                  class="mt-1"
                />
                <div v-else class="text-muted small">
                  {{ $t("bulk_buy.rfq_notice") }}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="col-xl-8">
          <div class="details-panel h-100">
            <div class="d-flex align-items-center mb-2">
              <FontAwesomeIcon icon="rectangle-list" class="text-info me-2" />
              <span class="text-uppercase text-muted fw-semibold section-label">
                {{ $t("company_orders.buy.purchase_summary") }}
              </span>
            </div>

            <div class="row g-2">
              <div
                v-for="item in overviewItems"
                :key="item.label"
                class="col-md-6"
              >
                <div class="detail-item h-100">
                  <div class="text-muted small mb-1">
                    {{ item.label }}
                  </div>
                  <div class="fw-semibold">
                    {{ item.value }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-if="hasSpecifications" class="details-panel mb-3">
        <div class="text-uppercase text-muted fw-semibold mb-3 section-label">
          {{ $t("company_orders.buy.go_specifications") }}
        </div>

        <div class="row g-2">
          <div
            v-for="item in specificationItems"
            :key="item.label"
            class="col-md-6 col-xl-4"
          >
            <div class="detail-item h-100">
              <div class="text-muted small mb-1">
                {{ item.label }}
              </div>
              <div class="fw-semibold">
                {{ item.value }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="row g-2 g-lg-3 mb-3">
        <div
          v-if="buyDetails.uploaded_file_id && buyDetails.company_id"
          :class="operationNotes ? 'col-xl-6' : 'col-12'"
        >
          <div class="details-panel h-100">
            <div class="d-flex align-items-center mb-2">
              <FontAwesomeIcon icon="file-contract" class="text-primary me-2" />
              <span class="text-uppercase text-muted fw-semibold section-label">
                {{ $t("company_orders.buy.uploaded_contract") }}
              </span>
            </div>

            <div class="detail-item detail-item-compact">
              <button
                type="button"
                class="btn btn-sm btn-outline-primary"
                @click="
                  downloadCompanyDocument(
                    buyDetails.company_id,
                    buyDetails.uploaded_file_id
                  )
                "
              >
                <FontAwesomeIcon icon="download" class="me-1" />
                {{ $t("common.download") }}
                {{ $t("company_orders.buy.contract") }}
              </button>
            </div>
          </div>
        </div>

        <div v-if="operationNotes" :class="hasContract ? 'col-xl-6' : 'col-12'">
          <div class="details-panel h-100">
            <div class="d-flex align-items-center mb-2">
              <FontAwesomeIcon icon="sticky-note" class="text-info me-2" />
              <span class="text-uppercase text-muted fw-semibold section-label">
                {{ $t("company_orders.buy.operation_notes") }}
              </span>
            </div>

            <div class="detail-item detail-item-compact">
              <p class="mb-0 small">{{ operationNotes }}</p>
            </div>
          </div>
        </div>
      </div>

      <div v-if="isAdmin && buyDetails.analysis_form_response" class="mb-3">
        <div class="alert alert-warning mb-0">
          <div class="d-flex align-items-center mb-2">
            <FontAwesomeIcon icon="robot" class="me-2" />
            <strong>Admin Only: AI Analysis Results</strong>
          </div>
          <pre class="mb-0 small">{{
            JSON.stringify(buyDetails.analysis_form_response, null, 2)
          }}</pre>
        </div>
      </div>
    </div>

    <GenericOrderDetailsSection v-else :details="details" />
  </div>
</template>

<script setup lang="ts">
import { formatEuro } from "@/composables/formatUtils";
import { useFileDownload } from "@/composables/useFileDownload";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { isAwaitingRfqQuote } from "../../common/companyOrderTypes";
import GenericOrderDetailsSection from "./GenericOrderDetailsSection.vue";
import RfqStatusTimeline from "./RfqStatusTimeline.vue";

interface Props {
  details: Record<string, any> | null | undefined;
  isAdmin?: boolean;
  /** Order creation timestamp; drives the RFQ timeline when the price is not yet quoted. */
  orderCreatedAt?: string | null;
  orderStatus?: string | null;
}

const props = defineProps<Props>();

const showRfqTimeline = computed(
  () => !!props.orderCreatedAt && isAwaitingRfqQuote(props.orderStatus)
);

const { t } = useI18n();
const { downloadCompanyDocument } = useFileDownload();

// Computed
const buyDetails = computed(() => {
  if (!props.details) return null;
  return props.details as any;
});

const toFiniteNumber = (value: unknown): number | undefined => {
  if (typeof value === "number") return isFinite(value) ? value : undefined;
  if (typeof value === "string") {
    const parsed = Number(value);
    return isFinite(parsed) ? parsed : undefined;
  }
  return undefined;
};

const displayTotalPrice = computed<number | undefined>(() => {
  if (!buyDetails.value) return undefined;
  return (
    toFiniteNumber(buyDetails.value.quoted_total_price) ??
    toFiniteNumber(buyDetails.value.total_price)
  );
});

const displayVintage = computed<string | null>(() => {
  const raw = buyDetails.value?.vintage;
  if (raw && String(raw).trim()) return String(raw);
  return null;
});

const overviewItems = computed(() => {
  const items = [
    {
      label: t("company_orders.buy.purchase_mode"),
      value: formatPurchaseMode(buyDetails.value?.purchase_mode),
    },
    {
      label: t("company_orders.buy.vintage"),
      value: displayVintage.value ?? "N/A",
    },
  ];

  return items;
});

const NO_PREFERENCE_VALUES = new Set(["ANY", "NONE", ""]);

const isMeaningful = (value: unknown): boolean => {
  const str = String(value ?? "")
    .trim()
    .toUpperCase();
  return !NO_PREFERENCE_VALUES.has(str);
};

const hasAdvancedOptions = computed(() => {
  if (!buyDetails.value) return false;
  const d = buyDetails.value;
  return (
    isMeaningful(d.eac_source) ||
    isMeaningful(d.technology) ||
    isMeaningful(d.ecolabel) ||
    isMeaningful(d.support) ||
    isMeaningful(d.cod_requirement)
  );
});

const matchingLabel = computed<string | null>(() => {
  if (!buyDetails.value) return null;
  if (buyDetails.value.hourly_matching) {
    return t("bulk_buy.matching_mode.hourly");
  }
  if (buyDetails.value.monthly_matching) {
    return t("bulk_buy.matching_mode.monthly");
  }
  return null;
});

const hasSpecifications = computed(
  () => hasAdvancedOptions.value || matchingLabel.value !== null
);

const specificationItems = computed(() => {
  const items = [
    {
      label: t("company_orders.buy.eac_source"),
      value: formatEnumLabel(buyDetails.value?.eac_source),
    },
    {
      label: t("company_orders.buy.technology"),
      value: formatEnumLabel(buyDetails.value?.technology),
    },
    {
      label: t("company_orders.buy.ecolabel"),
      value: formatEnumLabel(buyDetails.value?.ecolabel),
    },
    {
      label: t("company_orders.buy.support"),
      value: formatEnumLabel(buyDetails.value?.support),
    },
    {
      label: t("company_orders.buy.cod_requirement"),
      value: formatEnumLabel(buyDetails.value?.cod_requirement),
    },
  ];

  if (matchingLabel.value) {
    items.push({
      label: t("company_orders.buy.matching"),
      value: matchingLabel.value,
    });
  }

  return items;
});

const operationNotes = computed<string | null>(() => {
  return (buyDetails.value?.operation_notes as string) ?? null;
});

const hasContract = computed(() => {
  return !!(buyDetails.value?.uploaded_file_id && buyDetails.value?.company_id);
});

const pricePerMwh = computed<number | null>(() => {
  const total = displayTotalPrice.value;
  const volume = buyDetails.value?.volume;
  if (total === undefined || !volume) return null;
  return total / volume;
});

const formatPurchaseMode = (mode?: string): string => {
  if (!mode) return t("go_buy.modes.advanced");

  const modeKey = mode.toLowerCase();
  const validModes = ["advanced", "cheapest", "re100"];
  if (validModes.includes(modeKey)) {
    return t(`go_buy.modes.${modeKey}`);
  }
  return mode;
};

const ENUM_LABELS: Record<string, string> = {
  // COD requirements
  LT_5Y: "< 5 years",
  LT_6Y: "< 6 years",
  LT_10Y: "< 10 years",
  LE_15Y: "≤ 15 years",
  // EAC sources
  STANDARD_AIB: "Standard AIB",
  AIB_GRID_CONNECTED: "AIB Grid Connected",
  AIB_GRID_CONNECTED_NO_NORWAY_ICELAND:
    "AIB Grid Connected (excl. Norway & Iceland)",
  // Technologies
  HYDRO_WIND_SOLAR: "Hydro + Wind + Solar",
  WIND_SOLAR: "Wind + Solar",
  // Ecolabels
  EKO_ENERGY: "EKOenergy",
  OK_POWER: "OK Power",
  OK_POWER_NEW: "OK Power New",
};

const formatEnumLabel = (value: unknown): string => {
  const str = String(value ?? "").trim();
  if (!str || NO_PREFERENCE_VALUES.has(str.toUpperCase())) return "—";
  const upper = str.toUpperCase();
  if (ENUM_LABELS[upper]) return ENUM_LABELS[upper];
  return str
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};
</script>

<style scoped lang="scss">
@use "../order-details-shared";

.details-panel {
  height: 100%;
  overflow: hidden;
}

.details-panel-summary {
  background: var(--phoenix-primary-bg-subtle);
  border-color: var(--phoenix-primary-border-subtle);
}

.summary-detail-secondary {
  margin-top: 0.875rem;
}

.summary-detail-price {
  margin-top: 0.25rem;
}

.summary-detail-value {
  font-size: 1rem;
  font-weight: 700;
  color: var(--phoenix-primary-text-emphasis);
}

.summary-detail-value-muted {
  font-size: 0.72rem;
  font-weight: 500;
  color: var(--phoenix-tertiary-color);
}

.detail-item-compact {
  padding: 0.625rem 0.75rem;
}
</style>
